import React, { Fragment } from "react";
import { Typography, Box } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import MobileAbout from "./MobileAbout";
import DesktopAbout from "./DesktopAbout";
import useSystemHook from "./../../hooks/SystemHook";
import { COLORS } from "../../constants";
import DOMPurify from "dompurify";

const About = () => {
  const sanitizedMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };
  const { isMobile, isTablet, isDesktop } = useSystemHook();
  const fourBoxes = [
    {
      heading: "+ 70 Years",
      text: "+ 70 years of excellence",
      fontWeight: 300,
    },
    {
      heading: "+ 5,000 Customers",
      text: "+ 5,000 Customers",
      fontWeight: 300,
    },
    {
      heading: "+ 30,000 Products",
      text: "+ 30,000 Product range",
      fontWeight: 300,
    },
    {
      heading: "+ 12 Countries",
      text: "+ 12 Countries Serviced",
      fontWeight: 300,
    },
  ];

  const historySections = [
    {
      year: "1955",
      title: "The Beginning",
      text: `In early 1955, a young motor mechanic, Louis Raff, left smoggy Johannesburg with his wife and their two sons, to set up shop in ‘the countryside’ at Argus Motors, 
            a ramshackle filling station with a workshop and adjoining pondok on Great North Road in Brentwood Park, a few kilometers north of Benoni on the East Rand. 
            <br /><br />
            
            The ‘business’ was purchased for £800 from a private operator while the license to sell fuel was obtained from the Shell Company, 
            which in turn held the lease on the five-acre property on which Argus Motors was situated.`,
      image:
        "https://argusweb.azureedge.net/argus-web/about/about-beginning.webp",
    },
    {
      year: "1960",
      title: "From the Ground Up",
      text: `In what was zoned as a ‘sub-economic’ area, Brentwood Park proved a tough testing ground for Louis and Anne, 
            who lived ‘off-grid’, relying on candle power, a windmill and an outhouse to supply their utilities, 
            while a pre-war Onen lighting plant (a 3hp generator) provided unreliable and often dangerous voltage to power the four Argus Motors fuel pumps. 
            <br /><br />
            
            By the early 1960s Louis and Anne had four sons (Larry, Ronnie, Mark, and Chaim) and a singular mission to not only upgrade Argus Motors but also to own the land it was situated on. 
            To achieve this meant convincing the Shell Company board of directors in Johannesburg to install a new generator and provide the necessary loan capital to modernize the Argus Motors forecourt and workshop. 
            <br /><br />
            
            Faced with obdurate corporate red tape, Louis and Anne ‘stormed’ Shell House to present their business case—that Argus Motors had great potential in the growing Benoni district and, 
            with the necessary capital, could finally become a viable concern.`,
      image:
        "https://argusweb.azureedge.net/argus-web/about/about-ground-up.webp",
    },
    {
      year: "1970",
      title: "Into Distribution",
      text: `By the mid-1970s, both Larry and Ronnie were working in the family business which had established itself as the leading service station in the area, 
            thanks to Louis’ “honesty, generosity, and dedication to quality workmanship,” says Anne. 
            “Not only were we able to buy the land but also begin building new facilities on it which we could rent to other automotive businesses.” 
            <br /><br />
            
            Born with an entrepreneurial gift, Larry, then in his mid-20s, soon recognized the massive potential replacement parts could have. 
            Wholesale agency contracts were duly signed with leading ISO-accredited parts suppliers, 
            and Argus Motor Company took a giant leap forward into the highly competitive business of distributing aftermarket parts to retail motor spares shops. 
            <br /><br />
            
            Leading brands such as DOE, BETA, Argus Motoring, and X-APPEAL that are recognized today in the marketplace were registered. 
            As the 20th Century drew to a close, the Raff family and its loyal staff had developed Argus Motor Company into a leading southern African parts supplier, 
            with a distribution infrastructure servicing all the ASDC countries.`,
      image:
        "https://argusweb.azureedge.net/argus-web/about/about-distrobution.webp",
    },
    {
      year: "2018",
      title: "Continuing the Legacy",
      text: `In 2015, the family suffered the loss of both Louis and Anne and soon afterwards in 2018 Ronnie passed away. 
            Today, with Larry and Mark as directors, Argus Motor Company now enjoys its third generation of Raff talent in Louis and Anne’s grandchildren, 
            Moishe (COO), Baruch (CTO), and Shifra (CFO). 
            <br /><br />
            
            A massive warehouse equipped with high-tech inventory management and dispatching systems keeps a fleet of delivery bakkies busy five days a week, 
            while contracted courier companies deliver the long-distance orders from over 5000 Argus Motors customers. `,
      image: "https://argusweb.azureedge.net/argusblob01/web/argus-splash.jpg",
    },
    {
      year: "2025",
      title: "The Digital Expansion",
      text: `Building on more than a decade of hands-on experience in the online automotive retail space, 
            Argus Motor Company entered a new chapter with the launch of <b>Argus Online</b>. 
            From early adoption and growth across leading marketplaces such as 
            <a href="https://www.takealot.com/" target="_blank" rel="noopener noreferrer">Takealot</a>, 
            <a href="https://amazon.co.za/" target="_blank" rel="noopener noreferrer">Amazon</a>, 
            <a href="https://www.makro.co.za/" target="_blank" rel="noopener noreferrer">Makro</a>, 
            <a href="https://www.startmycar.co.za/" target="_blank" rel="noopener noreferrer">Start My Car</a>, and 
            <a href="https://www.bobshop.co.za/" target="_blank" rel="noopener noreferrer">Bob Shop</a>, 
            the business developed deep operational, logistical, and customer-centric expertise in e-commerce long before it became industry standard.

            <br /><br />

            By 2025, this accumulated knowledge culminated in the creation of <b>Argus Online</b>—a market-leading digital portal designed 
            to make finding and purchasing automotive replacement parts simpler, faster, and more reliable. 
            Argus Online brings together the company’s trusted product range, real-time availability, and decades of technical know-how into a single, purpose-built platform 
            that serves both retail and trade customers nationwide. 

            </br> </br>

            The launch reflects Argus Motor Company’s continued commitment to evolving with the industry while staying true to the values that have guided the business since 1955: 
            reliability, integrity, and long-term partnerships.`,
      image: "ArgusWeb.png",
    },
  ];

  const conclusionSections = [
    {
      title: "Our Philosophy & Commitment",
      text: `According to the team, “Our focus is on supplying quality replacement parts with exceptional backup service to the retail spares trade. 
      Our 5000 square meter warehouse has over 30,000 stock items of both leading and Argus-branded products covering the full spectrum of popular replacement parts and accessories.”
    
    <br /><br />
    
    With the credo, ‘people sell to people’ underpinning its day-to-day operations, superior customer service will always be the primary driver of Argus. 
    “When you deal with Argus Motor Company, you are assured of total customer satisfaction and service. You are dealing with a company that is steeped in business ethics and an ethos that you, the valued customer, is all important,” concludes Larry.`,
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs />
      <Typography
        sx={{
          paddingLeft: 0,
          backgroundColor: "none",
          fontSize: isMobile ? "var(--font-size-l)" : "var(--font-size-xl)",
          fontWeight: 500,
          my: 4,
        }}
      >
        About Us
      </Typography>
      {isMobile ? (
        <MobileAbout
          fourBoxes={fourBoxes}
          historySections={historySections}
          conclusionSections={conclusionSections}
        />
      ) : (
        <DesktopAbout
          fourBoxes={fourBoxes}
          historySections={historySections}
          conclusionSections={conclusionSections}
        />
      )}

      {/* Conclusion Section */}
      {conclusionSections?.map((section, index) => (
        <Box
          key={index}
          sx={{
            mt: 10,
            px: 4,
            py: 4,
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "2px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              mb: 3,
              fontSize: isMobile ? "var(--font-size-l)" : "var(--font-size-xl)",
              fontWeight: 600,
              color: COLORS.darkText,
            }}
          >
            {section.title}
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile
                ? "var(--font-size-xxs)"
                : "var(--font-size-s)",
              margin: "0 auto",
              color: COLORS.lightText,
            }}
            dangerouslySetInnerHTML={sanitizedMarkup(section.text)}
          />
        </Box>
      ))}
    </Fragment>
  );
};

export default About;
